// Module 10 — HRMS Extras (HR-07 Payslips, HR-10/11 Recruitment, HR-08/09 KPI)
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtPKR, fmtDate } from '../../utils/helpers';
import { Spinner, EmptyState, Modal } from '../../components/common';
import toast from 'react-hot-toast';
import { Plus, Download, Users, Briefcase, Edit2, Trash2 } from 'lucide-react';

// ── HR-07 Payslip Generator ───────────────────────────────────────────────────
export function PayslipsPage() {
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoad]      = useState(true);
  const [month, setMonth]       = useState(new Date().getMonth()+1);
  const [year, setYear]         = useState(new Date().getFullYear());

  const load = useCallback(async () => {
    setLoad(true);
    api.get('/hr/payroll?month='+month+'&year='+year+'&limit=100').then(r=>r.data)
      .then(r=>{ if(r.success) setPayrolls(r.data||[]); }).finally(()=>setLoad(false));
  },[month, year]);
  useEffect(()=>{ load(); },[load]);

  const printPayslip = (emp) => {
    const w = window.open('','_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Payslip</title>
    <style>body{font-family:Arial;font-size:12px;margin:0;padding:24px}
    .hdr{background:#1B2A4A;color:white;padding:16px 20px;margin-bottom:20px;border-radius:6px}
    table{width:100%;border-collapse:collapse;margin-bottom:16px}
    td,th{padding:8px;border:1px solid #e5e7eb}th{background:#f9fafb}
    .total{background:#1B2A4A;color:white;font-weight:bold}
    @media print{body{padding:8px}}</style></head><body>
    <div class="hdr"><div style="font-size:18px;font-weight:bold">ECL Express Courier & Logistics</div>
    <div style="opacity:.8;font-size:12px">Salary Slip — ${new Date(year,month-1).toLocaleString('en',{month:'long'})} ${year}</div></div>
    <table><tr><td><strong>Employee:</strong> ${emp.emp_name||emp.name}</td><td><strong>Emp Code:</strong> ${emp.emp_code||'—'}</td></tr>
    <tr><td><strong>Designation:</strong> ${emp.designation||'—'}</td><td><strong>Department:</strong> ${emp.department||'—'}</td></tr>
    <tr><td><strong>Joining Date:</strong> ${emp.joining_date?fmtDate(emp.joining_date):'—'}</td><td><strong>Bank Account:</strong> —</td></tr></table>
    <table><thead><tr><th>Earnings</th><th>Amount (PKR)</th><th>Deductions</th><th>Amount (PKR)</th></tr></thead>
    <tbody>
    <tr><td>Basic Salary</td><td>PKR ${parseFloat(emp.basic||0).toLocaleString()}</td><td>EOBI</td><td>PKR ${(parseFloat(emp.basic||0)*0.01).toFixed(0)}</td></tr>
    <tr><td>House Allowance (40%)</td><td>PKR ${(parseFloat(emp.basic||0)*0.4).toFixed(0)}</td><td>Income Tax</td><td>PKR ${parseFloat(emp.deductions||0).toFixed(0)}</td></tr>
    <tr><td>Transport Allowance</td><td>PKR ${(parseFloat(emp.basic||0)*0.1).toFixed(0)}</td><td></td><td></td></tr>
    </tbody>
    <tfoot><tr class="total"><td colspan="2">Gross: PKR ${(parseFloat(emp.basic||0)*1.5).toFixed(0)}</td>
    <td>Total Deductions:</td><td>PKR ${parseFloat(emp.deductions||0).toFixed(0)}</td></tr>
    <tr><td colspan="3" style="text-align:right;font-weight:bold">NET PAY:</td>
    <td style="font-weight:bold;font-size:14px">PKR ${parseFloat(emp.net||0).toLocaleString()}</td></tr></tfoot></table>
    <div style="margin-top:40px;display:flex;justify-content:space-between">
    <div style="text-align:center"><div style="border-top:1px solid #000;margin-top:30px;padding-top:4px">Employee Signature</div></div>
    <div style="text-align:center"><div style="border-top:1px solid #000;margin-top:30px;padding-top:4px">Authorized Signature</div></div></div>
    <script>window.onload=function(){window.print()}</script></body></html>`);
  };

  return (
    <AppShell title="Payslips" actions={
      <div style={{ display:'flex', gap:8 }}>
        <select className="form-control" style={{ width:130 }} value={month} onChange={e=>setMonth(parseInt(e.target.value))}>
          {Array.from({length:12},(_,i)=><option key={i+1} value={i+1}>{new Date(2000,i).toLocaleString('en',{month:'long'})}</option>)}
        </select>
        <input className="form-control" type="number" style={{ width:90 }} value={year} onChange={e=>setYear(parseInt(e.target.value))}/>
        <button className="btn btn-outline btn-sm" onClick={load}>Load</button>
      </div>
    }>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : payrolls.length ? (
            <table>
              <thead><tr><th>Employee</th><th>Basic</th><th>Deductions</th><th>Net Pay</th><th>Status</th><th>Payslip</th></tr></thead>
              <tbody>
                {payrolls.map((p,i)=>(
                  <tr key={i}>
                    <td><div style={{ fontWeight:500 }}>{p.emp_name}</div><div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{p.emp_code}</div></td>
                    <td>{fmtPKR(p.basic)}</td>
                    <td style={{ color:'var(--danger)' }}>{fmtPKR(p.deductions)}</td>
                    <td style={{ fontWeight:700, color:'var(--success)' }}>{fmtPKR(p.net)}</td>
                    <td><span className={`badge ${p.status==='FINALIZED'?'badge-success':p.status==='GENERATED'?'badge-info':'badge-gray'}`} style={{ fontSize:'.65rem' }}>{p.status}</span></td>
                    <td><button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }} onClick={()=>printPayslip(p)}><Download size={11}/> Print</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📄" title="No payroll data" message="Generate payroll first from Payroll page" />}
        </div>
      </div>
    </AppShell>
  );
}

// ── HR-10/11 Recruitment ──────────────────────────────────────────────────────
export function RecruitmentPage() {
  const [jobs, setJobs]         = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [tab, setTab]           = useState('jobs');
  const [loading, setLoad]      = useState(true);
  const [showJob, setShowJob]   = useState(false);
  const [jobEdit, setJobEdit]   = useState(null);
  const [jobForm, setJobForm]   = useState({ title:'', department:'', location:'', type:'FULL_TIME', description:'', openings:1 });
  const setJ = k => e => setJobForm(f=>({...f,[k]:e.target.value}));

  useEffect(()=>{
    setLoad(true);
    Promise.all([
      api.get('/hr/jobs?limit=50').then(r=>r.data).catch(()=>({ success:false, data:[] })),
      api.get('/hr/candidates?limit=50').then(r=>r.data).catch(()=>({ success:false, data:[] })),
    ]).then(([j,c])=>{ if(j.success) setJobs(j.data||[]); if(c.success) setCandidates(c.data||[]); }).finally(()=>setLoad(false));
  },[]);

  const createJob = async () => {
    if (!jobForm.title) { toast.error('Job title required'); return; }
    try {
      if (jobEdit) {
        await api.patch('/hr/jobs/'+jobEdit.job_id, jobForm);
        toast.success('Job updated');
      } else {
        await api.post('/hr/jobs', jobForm);
        toast.success('Job posting created');
      }
      setShowJob(false); setJobEdit(null);
    } catch(e) { toast.error(e.response?.data?.message||'API endpoint not yet enabled'); }
  };

  const STATUS_COLOR = { APPLIED:'var(--info)', SCREENING:'var(--amber)', INTERVIEW:'var(--navy)', OFFER:'var(--success)', HIRED:'var(--success)', REJECTED:'var(--danger)' };

  return (
    <AppShell title="Recruitment" actions={tab==='jobs'&&<button className="btn btn-primary btn-sm" onClick={()=>setShowJob(true)}><Plus size={14}/> Post Job</button>}>
      <div className="tabs" style={{ marginBottom:16 }}>
        <div className={'tab'+(tab==='jobs'?' active':'')} onClick={()=>setTab('jobs')}><Briefcase size={13} style={{ marginRight:6,display:'inline'}}/>Job Postings ({jobs.length})</div>
        <div className={'tab'+(tab==='candidates'?' active':'')} onClick={()=>setTab('candidates')}><Users size={13} style={{ marginRight:6,display:'inline'}}/>Candidates ({candidates.length})</div>
      </div>

      {tab==='jobs' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : jobs.length ? (
              <table>
                <thead><tr><th>Title</th><th>Department</th><th>Type</th><th>Openings</th><th>Applications</th><th>Status</th><th>Actions</th></tr></thead>
                <tbody>
                  {jobs.map(j=>(
                    <tr key={j.job_id}>
                      <td style={{ fontWeight:600 }}>{j.title}</td>
                      <td className="text-sm">{j.department}</td>
                      <td><span className="badge badge-info" style={{ fontSize:'.65rem' }}>{j.type}</span></td>
                      <td style={{ textAlign:'center' }}>{j.openings}</td>
                      <td style={{ textAlign:'center' }}>{j.application_count||0}</td>
                      <td><span className={`badge ${j.is_active?'badge-success':'badge-gray'}`} style={{ fontSize:'.65rem' }}>{j.is_active?'OPEN':'CLOSED'}</span></td>
                      <td><div style={{ display:'flex', gap:4 }}>
                        <button className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }}
                          onClick={()=>{ setJobEdit(j); setJobForm({ title:j.title, department:j.department||'', location:j.location||'', type:j.type||'FULL_TIME', description:j.description||'', openings:j.openings||1 }); setShowJob(true); }}>
                          <Edit2 size={11}/>
                        </button>
                        <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                          onClick={async()=>{ if(!window.confirm('Close job posting?')) return; try{ await api.patch('/hr/jobs/'+j.job_id,{ isActive:false }); toast.success('Job closed'); const r=await api.get('/hr/jobs?limit=50').then(r=>r.data); if(r.success) setJobs(r.data||[]); }catch(e){ toast.error(e.response?.data?.message||'Failed'); } }}>
                          <Trash2 size={11}/>
                        </button>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="💼" title="No job postings" message={<button className="btn btn-primary btn-sm" onClick={()=>setShowJob(true)}>Post First Job</button>}/>}
          </div>
        </div>
      )}

      {tab==='candidates' && (
        <div className="card">
          <div className="table-wrap">
            {loading ? <Spinner /> : candidates.length ? (
              <table>
                <thead><tr><th>Candidate</th><th>Position</th><th>Applied On</th><th>Stage</th><th>Score</th></tr></thead>
                <tbody>
                  {candidates.map(c=>(
                    <tr key={c.candidate_id}>
                      <td><div style={{ fontWeight:500 }}>{c.name}</div><div style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>{c.email}</div></td>
                      <td className="text-sm">{c.job_title||'—'}</td>
                      <td className="text-sm">{fmtDate(c.applied_at)}</td>
                      <td><span style={{ padding:'2px 8px', borderRadius:10, fontSize:'.65rem', fontWeight:600, color:STATUS_COLOR[c.stage]||'var(--gray-400)', background:(STATUS_COLOR[c.stage]||'#888')+'18' }}>{c.stage}</span></td>
                      <td>{c.score||'—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <EmptyState icon="👤" title="No candidates yet" />}
          </div>
        </div>
      )}

      <Modal open={showJob} onClose={()=>setShowJob(false)} title="Post New Job">
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Job Title <span className="req">*</span></label><input className="form-control" value={jobForm.title} onChange={setJ('title')}/></div>
            <div className="form-group"><label className="form-label">Department</label><input className="form-control" value={jobForm.department} onChange={setJ('department')}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Employment Type</label>
              <select className="form-control" value={jobForm.type} onChange={setJ('type')}>
                {['FULL_TIME','PART_TIME','CONTRACT','FREELANCE'].map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
              </select>
            </div>
            <div className="form-group"><label className="form-label">Openings</label><input className="form-control" type="number" min="1" value={jobForm.openings} onChange={setJ('openings')}/></div>
          </div>
          <div className="form-group"><label className="form-label">Location</label><input className="form-control" value={jobForm.location} onChange={setJ('location')}/></div>
          <div className="form-group"><label className="form-label">Description</label><textarea className="form-control" rows={4} value={jobForm.description} onChange={setJ('description')}/></div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={()=>setShowJob(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={createJob}>Post Job</button>
        </div>
      </Modal>
    </AppShell>
  );
}
